import React, { Component } from 'react';
import PropTypes from 'prop-types';
import SVGA from 'svgaplayerweb';
import './AnimatedImagePlayer.css';

class AnimatedImagePlayer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            player: null,
            isPlaying: false,
            isLoading: false,
            error: null,
            fileType: null
        };
        this.canvasRef = React.createRef();
        this.player = null;
        this.parser = null;
        this.videoRef = React.createRef();
    }

    componentDidMount() {
        if (this.props.animatedUrl) {
            this.detectFileTypeAndLoad(this.props.animatedUrl);
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.animatedUrl !== this.props.animatedUrl && this.props.animatedUrl) {
            this.detectFileTypeAndLoad(this.props.animatedUrl);
        }
    }

    componentWillUnmount() {
        this.cleanup();
    }

    detectFileType = (url) => {
        if (!url) return null;
        
        const extension = url.split('.').pop()?.toLowerCase();
        
        if (extension === 'svga') {
            return 'svga';
        } else if (extension === 'webp') {
            return 'webp';
        } else if (extension === 'gif') {
            return 'gif';
        } else if (['mp4', 'webm', 'ogg'].includes(extension)) {
            return 'video';
        } else {
            return 'image';
        }
    }

    detectFileTypeAndLoad = (url) => {
        const fileType = this.detectFileType(url);
        this.setState({ fileType });
        
        if (fileType === 'svga') {
            this.loadSVGA(url);
        } else if (fileType === 'webp') {
            this.loadWebP(url);
        } else if (fileType === 'gif') {
            this.loadGIF(url);
        } else if (fileType === 'video') {
            this.loadVideo(url);
        } else {
            this.loadImage(url);
        }
    }

    cleanup = () => {
        if (this.player) {
            this.player.clear();
            this.player = null;
        }
        if (this.parser) {
            this.parser = null;
        }
    }

    loadSVGA = (url) => {
        this.setState({ isLoading: true, error: null });
        
        try {
            this.cleanup();
            this.player = new SVGA.Player(this.canvasRef.current);
            this.parser = new SVGA.Parser(this.canvasRef.current);

            this.parser.load(url, (videoItem) => {
                if (videoItem) {
                    this.player.setVideoItem(videoItem);
                    this.setState({ 
                        isLoading: false, 
                        isPlaying: false,
                        error: null 
                    });
                    
                    if (this.props.autoPlay) {
                        this.startAnimation();
                    }
                } else {
                    this.setState({ 
                        isLoading: false, 
                        error: 'Không thể load SVGA animation' 
                    });
                }
            }, (error) => {
                console.error('SVGA loading error:', error);
                this.setState({ 
                    isLoading: false, 
                    error: 'Lỗi khi tải SVGA animation' 
                });
            });

        } catch (error) {
            console.error('SVGA initialization error:', error);
            this.setState({ 
                isLoading: false, 
                error: 'Lỗi khởi tạo SVGA player' 
            });
        }
    }

    loadWebP = (url) => {
        this.setState({ isLoading: true, error: null });
        
        const img = new Image();
        img.onload = () => {
            this.setState({ 
                isLoading: false, 
                isPlaying: false,
                error: null 
            });
        };
        
        img.onerror = () => {
            this.setState({ 
                isLoading: false, 
                error: 'Không thể load WebP image' 
            });
        };
        
        img.src = url;
    }

    loadGIF = (url) => {
        this.setState({ isLoading: true, error: null });
        
        const img = new Image();
        img.onload = () => {
            this.setState({ 
                isLoading: false, 
                isPlaying: false,
                error: null 
            });
        };
        
        img.onerror = () => {
            this.setState({ 
                isLoading: false, 
                error: 'Không thể load GIF image' 
            });
        };
        
        img.src = url;
    }

    loadVideo = (url) => {
        this.setState({ isLoading: true, error: null });
        
        if (this.videoRef.current) {
            this.videoRef.current.src = url;
            this.videoRef.current.onloadeddata = () => {
                this.setState({ 
                    isLoading: false, 
                    isPlaying: false,
                    error: null 
                });
                
                if (this.props.autoPlay) {
                    this.videoRef.current.play();
                    this.setState({ isPlaying: true });
                }
            };
            
            this.videoRef.current.onerror = () => {
                this.setState({ 
                    isLoading: false, 
                    error: 'Không thể load video' 
                });
            };
        }
    }

    loadImage = (url) => {
        this.setState({ isLoading: true, error: null });
        
        const img = new Image();
        img.onload = () => {
            this.setState({ 
                isLoading: false, 
                isPlaying: false,
                error: null 
            });
        };
        
        img.onerror = () => {
            this.setState({ 
                isLoading: false, 
                error: 'Không thể load image' 
            });
        };
        
        img.src = url;
    }

    startAnimation = () => {
        const { fileType } = this.state;
        
        if (fileType === 'svga' && this.player) {
            this.player.startAnimation();
            this.setState({ isPlaying: true });
        } else if (fileType === 'video' && this.videoRef.current) {
            this.videoRef.current.play();
            this.setState({ isPlaying: true });
        }
    }

    stopAnimation = () => {
        const { fileType } = this.state;
        
        if (fileType === 'svga' && this.player) {
            this.player.stopAnimation();
            this.setState({ isPlaying: false });
        } else if (fileType === 'video' && this.videoRef.current) {
            this.videoRef.current.pause();
            this.setState({ isPlaying: false });
        }
    }

    pauseAnimation = () => {
        const { fileType } = this.state;
        
        if (fileType === 'svga' && this.player) {
            this.player.pauseAnimation();
            this.setState({ isPlaying: false });
        } else if (fileType === 'video' && this.videoRef.current) {
            this.videoRef.current.pause();
            this.setState({ isPlaying: false });
        }
    }

    handleCanvasClick = () => {
        if (this.props.onClick) {
            this.props.onClick({
                animatedUrl: this.props.animatedUrl,
                giftName: this.props.giftName,
                isPlaying: this.state.isPlaying,
                fileType: this.state.fileType
            });
        }
    }

    handlePlayPause = () => {
        if (this.state.isPlaying) {
            this.pauseAnimation();
        } else {
            this.startAnimation();
        }
    }

    renderContent = () => {
        const { animatedUrl, giftName } = this.props;
        const { fileType, isLoading, error } = this.state;

        if (isLoading) {
            return (
                <div className="animated-loading">
                    <div className="loading-spinner"></div>
                    <span>Đang tải...</span>
                </div>
            );
        }

        if (error) {
            return (
                <div className="animated-error">
                    <span>⚠️ {error}</span>
                </div>
            );
        }

        switch (fileType) {
            case 'svga':
                return <div ref={this.canvasRef} className="animated-canvas" />;
            
            case 'webp':
            case 'gif':
            case 'image':
                return (
                    <img 
                        src={animatedUrl} 
                        alt={giftName}
                        className="animated-image"
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                );
            
            case 'video':
                return (
                    <video
                        ref={this.videoRef}
                        className="animated-video"
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        muted
                        loop
                        playsInline
                    />
                );
            
            default:
                return (
                    <div className="animated-placeholder">
                        <span>Không hỗ trợ định dạng này</span>
                    </div>
                );
        }
    }

    render() {
        const { 
            width = '100%', 
            height = '100%', 
            className = '',
            showControls = false,
            giftName = 'Animation'
        } = this.props;

        const { isPlaying, fileType } = this.state;

        const isAnimated = ['svga', 'gif', 'video'].includes(fileType);

        return (
            <div 
                className={`animated-player-container ${className}`}
                data-file-type={fileType}
            >
                <div 
                    className="animated-content"
                    style={{
                        width: width,
                        height: height,
                        cursor: this.props.onClick ? 'pointer' : 'default'
                    }}
                    onClick={this.handleCanvasClick}
                    title={giftName}
                >
                    {this.renderContent()}
                </div>
                
                {showControls && isAnimated && !this.state.isLoading && !this.state.error && (
                    <div className="animated-controls">
                        <button 
                            className="animated-control-btn"
                            onClick={this.handlePlayPause}
                            title={isPlaying ? 'Tạm dừng' : 'Phát'}
                        >
                            {isPlaying ? '⏸️' : '▶️'}
                        </button>
                    </div>
                )}
            </div>
        );
    }
}

AnimatedImagePlayer.propTypes = {
    animatedUrl: PropTypes.string,
    giftName: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
    showControls: PropTypes.bool,
    autoPlay: PropTypes.bool,
    onClick: PropTypes.func
};

AnimatedImagePlayer.defaultProps = {
    showControls: false,
    autoPlay: false
};

export default AnimatedImagePlayer; 